import { FC, useEffect, useId, useMemo, useRef, useState } from 'react';
import { VehicleBrand, fetchVehicleBrands } from '../api/vehicleBrands';

interface SelectVehicleBrandProps {
  value: string;
  onChange: (brandId: string, brand: VehicleBrand | null) => void;
  id?: string;
  name?: string;
  label?: string;
  labelClassName?: string;
  className?: string;
  selectClassName?: string;
  helperText?: string;
  helperClassName?: string;
  placeholderLabel?: string;
  placeholderValue?: string;
  includePlaceholder?: boolean;
  disabled?: boolean;
  required?: boolean;
  limit?: number;
  renderOptionLabel?: (brand: VehicleBrand) => string;
  onOptionsLoaded?: (brands: VehicleBrand[]) => void;
  onStatusChange?: (status: { loading: boolean; error: string | null; brands: VehicleBrand[] }) => void;
  refreshKey?: string | number;
  showStatus?: boolean;
}

interface CachedBrandsState {
  data: VehicleBrand[] | null;
  error: string | null;
}

let cachedState: CachedBrandsState = {
  data: null,
  error: null,
};

let pendingRequest: Promise<VehicleBrand[]> | null = null;

const joinClasses = (...classes: Array<string | undefined | null | false>) =>
  classes.filter(Boolean).join(' ');

const clampLimit = (limit: number) => {
  if (!Number.isFinite(limit) || limit <= 0) {
    return 100;
  }
  return Math.min(Math.floor(limit), 100);
};

const fetchAllVehicleBrands = async (limit: number): Promise<VehicleBrand[]> => {
  const pageSize = clampLimit(limit);
  const first = await fetchVehicleBrands({ page: 1, limit: pageSize });
  let aggregated = [...first.data];
  const totalPages = first.meta?.totalPages ?? 1;

  for (let page = 2; page <= totalPages; page += 1) {
    const next = await fetchVehicleBrands({ page, limit: pageSize });
    aggregated = aggregated.concat(next.data);
  }

  return aggregated;
};

const ensureVehicleBrands = async (limit: number): Promise<VehicleBrand[]> => {
  if (cachedState.data) {
    return cachedState.data;
  }

  if (pendingRequest) {
    return pendingRequest;
  }

  pendingRequest = fetchAllVehicleBrands(limit)
    .then((data) => {
      cachedState = {
        data,
        error: null,
      };
      return data;
    })
    .catch((error) => {
      const message =
        error instanceof Error ? error.message : 'Failed to load vehicle brands.';
      cachedState = {
        data: null,
        error: message,
      };
      throw new Error(message);
    })
    .finally(() => {
      pendingRequest = null;
    });

  return pendingRequest;
};

const SelectVehicleBrand: FC<SelectVehicleBrandProps> = ({
  value,
  onChange,
  id,
  name,
  label,
  labelClassName,
  className,
  selectClassName,
  helperText,
  helperClassName,
  placeholderLabel = 'Select a brand',
  placeholderValue = '',
  includePlaceholder = true,
  disabled = false,
  required = false,
  limit = 100,
  renderOptionLabel,
  onOptionsLoaded,
  onStatusChange,
  refreshKey,
  showStatus = true,
}) => {
  const generatedId = useId();
  const selectId = id ?? `vehicle-brand-${generatedId}`;

  const [brands, setBrands] = useState<VehicleBrand[]>(() => cachedState.data ?? []);
  const [loading, setLoading] = useState<boolean>(() => !cachedState.data && !cachedState.error);
  const [error, setError] = useState<string | null>(() => cachedState.error);
  const lastRefreshKeyRef = useRef<string | number | undefined>(refreshKey);

  useEffect(() => {
    onStatusChange?.({ loading, error, brands });
  }, [brands, error, loading, onStatusChange]);

  useEffect(() => {
    if (brands.length > 0) {
      onOptionsLoaded?.(brands);
    }
  }, [brands, onOptionsLoaded]);

  useEffect(() => {
    const refreshChanged = refreshKey !== undefined && refreshKey !== lastRefreshKeyRef.current;

    if (refreshChanged) {
      cachedState = { data: null, error: null };
    }

    lastRefreshKeyRef.current = refreshKey;

    if (cachedState.data || cachedState.error) {
      setBrands(cachedState.data ?? []);
      setLoading(false);
      setError(cachedState.error);
      onStatusChange?.({ loading: false, error: cachedState.error, brands: cachedState.data ?? [] });
      if (cachedState.data) {
        onOptionsLoaded?.(cachedState.data);
      }
      return;
    }

    let active = true;

    setLoading(true);
    setError(null);
    onStatusChange?.({ loading: true, error: null, brands: [] });

    ensureVehicleBrands(limit)
      .then((data) => {
        if (!active) {
          return;
        }
        setBrands(data);
        setLoading(false);
        setError(null);
        onStatusChange?.({ loading: false, error: null, brands: data });
        onOptionsLoaded?.(data);
      })
      .catch((fetchError) => {
        if (!active) {
          return;
        }
        const message =
          fetchError instanceof Error ? fetchError.message : 'Failed to load vehicle brands.';
        setBrands([]);
        setLoading(false);
        setError(message);
        onStatusChange?.({ loading: false, error: message, brands: [] });
      });

    return () => {
      active = false;
    };
  }, [limit, onOptionsLoaded, refreshKey, onStatusChange]);

  const combinedHelperText = useMemo(() => {
    if (loading) {
      return 'Loading vehicle brands…';
    }
    if (error) {
      return error;
    }
    return helperText ?? null;
  }, [error, helperText, loading]);

  return (
    <div className={joinClasses('flex flex-col gap-1', className)}>
      {label ? (
        <label htmlFor={selectId} className={joinClasses('text-xs font-semibold text-slate-600', labelClassName)}>
          {label}
        </label>
      ) : null}
      <select
        id={selectId}
        name={name}
        value={value}
        onChange={(event) => {
          const nextValue = event.target.value;
          const brand = brands.find((item) => item.id === nextValue) ?? null;
          onChange(nextValue, brand);
        }}
        disabled={disabled || loading}
        required={required}
        className={joinClasses(
          'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-slate-100',
          selectClassName,
        )}
      >
        {includePlaceholder ? (
          <option value={placeholderValue}>{placeholderLabel}</option>
        ) : null}
        {brands.map((brand) => (
          <option key={brand.id} value={brand.id}>
            {renderOptionLabel ? renderOptionLabel(brand) : brand.name}
          </option>
        ))}
      </select>
      {showStatus && combinedHelperText ? (
        <p
          className={joinClasses(
            'text-[11px] text-slate-400',
            error ? 'text-danger' : undefined,
            helperClassName,
          )}
        >
          {combinedHelperText}
        </p>
      ) : null}
    </div>
  );
};

export default SelectVehicleBrand;
